import { useApp } from '@/contexts';

import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { logger } from '@/utils/logger';

import { useCreateScenarioMutation, useGetScenarioListQuery } from '../../api/wizardsApi';

interface WizardScenarioStepProps {
  typeGuid: string;
  onNext: (scenarioId: string) => void;
  onSaveAndExit?: () => void;
  initialScenarioId?: string;
}

export const WizardScenarioStep = ({
  typeGuid,
  onNext,
  onSaveAndExit,
  initialScenarioId,
}: WizardScenarioStepProps) => {
  const { appId } = useApp();
  const [selectedScenario, setSelectedScenario] = useState(initialScenarioId || '');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDescription, setNewScenarioDescription] = useState('');

  // Fetch scenarios (will need tenant/account context)
  const { data: scenarios = [], isLoading } = useGetScenarioListQuery({
    tenantId: 'tenant-id', // TODO: Get from auth context
    accountId: appId,
    typeGuid,
  });

  const [createScenario, { isLoading: isCreating }] = useCreateScenarioMutation();

  const handleCreateScenario = async () => {
    try {
      const newScenario = await createScenario({
        name: newScenarioName,
        description: newScenarioDescription,
        typeGuid,
        tenantId: 'tenant-id', // TODO: Get from auth context
        accountId: appId,
      }).unwrap();

      setSelectedScenario(newScenario.id);
      setIsCreatingNew(false);
      setNewScenarioName('');
      setNewScenarioDescription('');
    } catch (err) {
      logger.error('Failed to create scenario:', err);
    }
  };

  const handleNext = () => {
    if (selectedScenario) {
      onNext(selectedScenario);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Scenario
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose an existing scenario or create a new one
      </Typography>

      {!isCreatingNew ? (
        <>
          <TextField
            select
            fullWidth
            label="Scenario"
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
            sx={{ mb: 2 }}
          >
            {scenarios.map((scenario) => (
              <MenuItem key={scenario.id} value={scenario.id}>
                {scenario.name}
              </MenuItem>
            ))}
          </TextField>

          <Button variant="outlined" onClick={() => setIsCreatingNew(true)} sx={{ mb: 3 }}>
            Create New Scenario
          </Button>
        </>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Creating a new scenario
          </Alert>

          <TextField
            fullWidth
            label="Scenario Name"
            value={newScenarioName}
            onChange={(e) => setNewScenarioName(e.target.value)}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={newScenarioDescription}
            onChange={(e) => setNewScenarioDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleCreateScenario}
              disabled={!newScenarioName || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
            <Button variant="outlined" onClick={() => setIsCreatingNew(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        {onSaveAndExit && (
          <Button variant="outlined" onClick={onSaveAndExit}>
            Save and Exit
          </Button>
        )}
        <Button variant="contained" onClick={handleNext} disabled={!selectedScenario}>
          Next
        </Button>
      </Box>
    </Box>
  );
};
